import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Plus, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import { listTemplates, createTemplate, approveTemplate, archiveTemplate, generateAiVariation, listVariations, approveVariation } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/templates")({
  head: () => ({ meta: [{ title: "Templates · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

const OPTOUT = "Reply NO to opt out .";

function Page() {
  const { primaryRole } = useAuth();
  const qc = useQueryClient();
  const list = useServerFn(listTemplates);
  const create = useServerFn(createTemplate);
  const approve = useServerFn(approveTemplate);
  const archive = useServerFn(archiveTemplate);
  const aiGen = useServerFn(generateAiVariation);

  const { data, isLoading } = useQuery({ queryKey: ["sms-templates"], queryFn: () => list(), enabled: primaryRole === "admin" });

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [body, setBody] = useState(`Heallexa: Your practice {{practice_name}} is listed in our directory. Claim or update it here: {{claim_link}}. ${OPTOUT}`);

  const createMut = useMutation({
    mutationFn: () => create({ data: { name, body } }),
    onSuccess: () => { toast.success("Template created"); setOpen(false); setName(""); qc.invalidateQueries({ queryKey: ["sms-templates"] }); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });
  const approveMut = useMutation({ mutationFn: (id: string) => approve({ data: { id } }), onSuccess: () => { toast.success("Approved"); qc.invalidateQueries({ queryKey: ["sms-templates"] }); } });
  const archiveMut = useMutation({ mutationFn: (id: string) => archive({ data: { id } }), onSuccess: () => { toast.success("Archived"); qc.invalidateQueries({ queryKey: ["sms-templates"] }); } });
  const aiMut = useMutation({
    mutationFn: (templateId: string) => aiGen({ data: { templateId } }),
    onSuccess: () => { toast.success("AI variation drafted"); qc.invalidateQueries(); },
    onError: (e: any) => toast.error(e.message ?? "AI failed"),
  });

  return (
    <SmsHubLayout title="Message Templates">
      <div className="flex justify-between items-center mb-3">
        <p className="text-[12px] text-muted-foreground">Every body must end with the exact sentence: <code className="px-1 bg-muted rounded">{OPTOUT}</code></p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-3.5 w-3.5 mr-1.5" />New template</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div>
                <Label>Body</Label>
                <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
                <p className="text-[11px] text-muted-foreground mt-1">Use {"{{practice_name}}"} and {"{{claim_link}}"}. Length {body.length}/1600. Ends with opt-out: {body.trimEnd().endsWith(OPTOUT) ? "✓" : "✗"}</p>
              </div>
              <Button disabled={createMut.isPending || !name || !body.trimEnd().endsWith(OPTOUT)} onClick={() => createMut.mutate()} className="w-full">
                {createMut.isPending ? "Creating…" : "Create as draft"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && <div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>}
      <div className="space-y-3">
        {(data ?? []).map((t: any) => (
          <TemplateCard key={t.id} t={t} onApprove={() => approveMut.mutate(t.id)} onArchive={() => archiveMut.mutate(t.id)} onAi={() => aiMut.mutate(t.id)} aiPending={aiMut.isPending} />
        ))}
      </div>
    </SmsHubLayout>
  );
}

function TemplateCard({ t, onApprove, onArchive, onAi, aiPending }: { t: any; onApprove: () => void; onArchive: () => void; onAi: () => void; aiPending: boolean }) {
  const listV = useServerFn(listVariations);
  const approveV = useServerFn(approveVariation);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: vars } = useQuery({ queryKey: ["sms-variations", t.id, open], queryFn: () => listV({ data: { templateId: t.id } }), enabled: open });
  const approveVm = useMutation({ mutationFn: (id: string) => approveV({ data: { id } }), onSuccess: () => { toast.success("Variation approved"); qc.invalidateQueries({ queryKey: ["sms-variations", t.id] }); } });

  return (
    <div className="border border-border rounded-md bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-[14px] font-semibold">{t.name}</h4>
            {t.is_base && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary">BASE</span>}
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.status === "approved" ? "bg-emerald-500/15 text-emerald-700" : "bg-muted"}`}>{t.status}</span>
          </div>
          <p className="text-[12.5px] text-muted-foreground mt-1.5 font-mono whitespace-pre-wrap">{t.body}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          {t.status !== "approved" && <Button size="sm" variant="outline" onClick={onApprove}><Check className="h-3 w-3 mr-1" />Approve</Button>}
          {t.status === "approved" && !t.is_base && <Button size="sm" variant="outline" onClick={onArchive}>Archive</Button>}
          <Button size="sm" variant="outline" onClick={onAi} disabled={aiPending}><Sparkles className="h-3 w-3 mr-1" />AI variation</Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Variations"}</Button>
        </div>
      </div>
      {open && (
        <div className="mt-3 pl-4 border-l-2 border-border space-y-2">
          {(vars ?? []).length === 0 && <p className="text-[12px] text-muted-foreground">No variations.</p>}
          {(vars ?? []).map((v: any) => (
            <div key={v.id} className="text-[12px] flex items-start gap-2">
              <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${v.status === "approved" ? "bg-emerald-500/15 text-emerald-700" : "bg-muted"}`}>{v.status}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted shrink-0">{v.source}</span>
              <p className="flex-1 font-mono">{v.body}</p>
              {v.status !== "approved" && <Button size="sm" variant="outline" onClick={() => approveVm.mutate(v.id)}>Approve</Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
